import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import {
  brandKnowledgeApi, BrandJob, MediaState, MonthlyPlan, OnboardingState, PlanState, Production, WebsiteRead,
} from '@/services/brandKnowledgeApi';
import type { CalendarItem } from '@/components/growth-desk/data/schedule';
import type { DeskJob } from '../ui';
import { DESK } from '../links';
import { CampaignNumbers, DeskCampaign, DeskPost, money, monthBounds, monthLabel, planCampaigns, planPosts, shortDate, thisMonth } from './status';

/**
 * Everything the Growth Desk shell and "This month" need, for one month: the plan and its
 * campaigns (joined with production, scheduled publishes and analytics), the background jobs,
 * and what needs the owner. Polls while anything runs; reloads when the business changes.
 */
export interface NeedItem {
  id: string;
  kind: 'question' | 'failed' | 'review' | 'plan' | 'strategy' | 'prepare' | 'facts' | 'launch' | 'missed';
  chip: string;
  action?: string;        // the button's words, when not the kind's usual ones
  title: string;
  detail?: string;
  to?: string;
  production?: Production;
  urgent: boolean;
}

export interface DeskState {
  month: string;
  loading: boolean;
  error: string | null;
  planState: PlanState | null;
  onboarding: OnboardingState | null;
  productions: Production[];
  campaigns: DeskCampaign[];
  posts: DeskPost[];
  scheduled: CalendarItem[];
  mediaCount: number | null;
  jobs: DeskJob[];
  /** The latest website read, whatever its status (Your business and Start show it). */
  websiteRead: WebsiteRead | null;
  /** The month to plan next (this one while campaigns have time to learn, else next month) and its plan. */
  nextPlan: { month: string; plan: MonthlyPlan | null } | null;
  needs: NeedItem[];
  spend: { spent: number; committed: number; planned: number; currency: string; known: boolean };
}

interface DeskContextValue extends DeskState {
  /** The month just asked for (the month arrows show it at once). `month` is the month whose data
   *  is shown: it moves to the asked-for month only when that month has loaded, so a page never
   *  mixes one month's name with another month's data, and doesn't blank out while switching. */
  requestedMonth: string;
  setMonth: (m: string) => void;
  refresh: () => Promise<void>;
}

const DeskContext = createContext<DeskContextValue | null>(null);

export const useDesk = () => {
  const ctx = useContext(DeskContext);
  if (!ctx) throw new Error('useDesk must be used inside <DeskProvider>');
  return ctx;
};

const safe = async <T,>(p: Promise<T>, fallback: T): Promise<T> => { try { return await p; } catch { return fallback; } };

const toNumbers = (c: any): CampaignNumbers => ({
  spend: c.spend ?? null, results: c.result_count ?? null, costPerResult: c.cost_per_result ?? null,
  currency: c.currency ?? null, resultType: c.result_type ?? null, delivering: c.delivering ?? null, status: c.status ?? null,
});

const jobFromBrand = (j: BrandJob | null, kind: 'strategy' | 'plan'): DeskJob | null => {
  if (!j || (j.status !== 'queued' && j.status !== 'running')) return null;
  return kind === 'strategy'
    ? { id: j.id, title: 'Writing your strategy', eta: '1–3 min', progress: null, detail: 'From your confirmed facts, then checked by a second review', to: DESK.strategy }
    : { id: j.id, title: `Writing your ${j.params?.month ? monthLabel(j.params.month, false) : ''} plan`.replace('  ', ' '), eta: '1–2 min', progress: null, detail: 'Checking it against your approved strategy', to: DESK.plan(j.params?.month) };
};

const siteHost = (url: string) => { try { return new URL(url).host; } catch { return url; } };

const jobFromWebsite = (j: WebsiteRead | null): DeskJob | null =>
  j && (j.status === 'queued' || j.status === 'running')
    ? { id: j.id, title: `Reading ${siteHost(j.params.url)}`, eta: '10–40 s', progress: null, detail: 'What you do, prices, offers, tone, colours and logo', to: `${DESK.business}?topic=website` }
    : null;

const jobFromProduction = (p: Production, name: string, month: string): DeskJob | null => {
  if (p.status === 'running') {
    const what = p.current?.format ? ` · ${p.current.format}` : '';
    return {
      id: p.id, title: name, eta: p.minutes_left ? `~${p.minutes_left} min` : null,
      progress: p.total ? Math.min(1, (p.done + 0.5) / p.total) : null,
      detail: p.total ? `Making ad ${Math.min(p.done + 1, p.total)} of ${p.total}${what}` : p.step_label, to: DESK.production(month),
    };
  }
  if (p.status === 'queued') return { id: p.id, title: name, progress: 0, detail: 'Waiting · starts when a slot frees up', to: DESK.production(month) };
  return null;
};

/** A production's name: its brief's, or its post's. */
const nameIn = (plan: MonthlyPlan | null | undefined) => (p: Production) =>
  (p.post_id ? plan?.posts?.find(x => x.id === p.post_id)?.name : plan?.briefs.find(b => b.id === p.brief_id)?.brief.name)
  || (p.post_id ? 'A post' : 'A campaign');

const daysTo = (d: Date) => Math.round((d.getTime() - new Date(new Date().toDateString()).getTime()) / 86400000);

/** What one month's plan needs from the owner. */
const planNeeds = (plan: MonthlyPlan, productions: Production[], campaigns: DeskCampaign[], posts: DeskPost[], m: string): NeedItem[] => {
  const needs: NeedItem[] = [];
  const nameOf = nameIn(plan);
  for (const p of productions.filter(x => x.status === 'needs_input'))
    needs.push({ id: `q-${p.id}`, kind: 'question', chip: 'Question from production', title: p.question?.text || `${nameOf(p)} has a question`, detail: `${nameOf(p)} · paused until you answer`, production: p, urgent: true, to: DESK.production(m, p.id) });
  for (const p of productions.filter(x => x.status === 'failed'))
    needs.push({ id: `f-${p.id}`, kind: 'failed', chip: 'Stopped', title: `${nameOf(p)} stopped`, detail: p.error || 'Retry continues from where it stopped.', production: p, urgent: true, to: DESK.production(m, p.id) });
  for (const c of campaigns.filter(x => x.status === 'needs_you' && x.statusDetail === 'publishing failed'))
    needs.push({ id: `pub-${c.row.id}`, kind: 'failed', chip: 'Launch problem', title: `${c.row.brief.name} wasn’t published`, detail: 'Open its Launch step to see why and fix it.', to: c.to, urgent: true });
  for (const c of campaigns.filter(x => x.statusDetail === 'missed'))
    needs.push({ id: `missed-${c.row.id}`, kind: 'missed', chip: 'Missed its start', title: `${c.row.brief.name} was due ${shortDate(c.start)}`,
      detail: c.barLabel.split(' · ')[1] || 'Move it to new dates or skip it.', to: c.to, urgent: true,
      action: c.to.startsWith('/month/') ? 'Move or skip' : 'Open it' });
  for (const c of campaigns.filter(x => x.status === 'approved' && x.production?.campaign_id)) {
    const days = daysTo(c.start);
    needs.push({ id: `launch-${c.row.id}`, kind: 'launch', chip: 'Not launched', title: `${c.row.brief.name} is approved but not launched`,
      detail: `Starts ${days <= 0 ? 'today' : shortDate(c.start)}. Confirm the ad account in its Launch step.`, to: DESK.campaign(c.production!.campaign_id!),
      urgent: days <= 2, action: 'Open the Launch step' });
  }
  for (const x of posts.filter(x => x.state === 'missed'))
    needs.push({ id: `missed-${x.row.id}`, kind: 'missed', chip: 'Missed its date', title: `${x.row.name} didn’t go out`, detail: 'Move it to a new date or skip it.',
      to: DESK.plan(m), urgent: true, action: 'Move or skip' });
  for (const x of posts.filter(x => x.state === 'failed'))
    needs.push({ id: `postfail-${x.row.id}`, kind: 'failed', chip: 'Posting failed', title: `${x.row.name} wasn’t posted`, detail: 'Open it to see why and schedule it again.',
      to: x.to, urgent: true });
  for (const x of posts.filter(x => x.state === 'ready' && x.production))
    needs.push({ id: `r-${x.production!.id}`, kind: 'review', chip: 'Post ready', title: `${x.row.name} is ready`, detail: `Goes out ${shortDate(x.date)} once you approve it.`,
      production: x.production!, to: DESK.postReview(x.production!.id), urgent: daysTo(x.date) <= 1, action: 'Review post' });
  for (const p of productions.filter(x => x.status === 'ready' && !x.approved_at && !x.post_id)) {
    const c = campaigns.find(x => x.production?.id === p.id);
    const sizes = new Set(p.ads.map(a => a.format)).size;
    needs.push({ id: `r-${p.id}`, kind: 'review', chip: 'Ready for review', title: `${nameOf(p)} — ${p.ads.length} ad${p.ads.length === 1 ? '' : 's'} in ${sizes} size${sizes === 1 ? '' : 's'}`,
      detail: `${c?.row.brief.budget ? `${money(c.row.brief.budget, plan.context.currency || 'INR')} budget · ` : ''}You approve, then pick the ad account.`,
      production: p, to: DESK.review(p.id), urgent: false });
  }
  if (plan.stale)          // the strategy (or what the plan rests on) changed: the plan waits for a refresh
    needs.push({ id: `plan-stale-${plan.id}`, kind: 'plan', chip: 'Out of date', action: 'Refresh plan',
      title: `Your ${monthLabel(m, false)} plan needs a refresh`, detail: `${plan.stale} Refresh it to keep working on it.`, to: DESK.plan(m), urgent: true });
  else if (plan.status === 'proposed')
    needs.push({ id: `plan-${plan.id}`, kind: 'plan', chip: 'Plan waiting', title: `Your ${monthLabel(m, false)} plan is ready for your OK`, detail: `${plan.briefs.length} campaign${plan.briefs.length === 1 ? '' : 's'} and ${plan.plan.organic_posts.length} post ideas.`, to: DESK.plan(m), urgent: false });
  if (plan.status === 'approved') {
    const notMade = campaigns.filter(c => c.status === 'planned' && !c.production).length;
    const postsNotMade = posts.filter(x => x.state === 'not_started').length;
    const what = [notMade && `${notMade} campaign${notMade === 1 ? '' : 's'}`, postsNotMade && `${postsNotMade} post${postsNotMade === 1 ? '' : 's'}`].filter(Boolean).join(' and ');
    if (what) needs.push({ id: `prepare-${m}`, kind: 'prepare', chip: 'Not started', title: `${what} approved but not made yet`,
      detail: plan.auto_produce?.on ? 'Each is made a few days before its date.' : 'Confirm the photos and the cost, then start production.', to: DESK.prepare(m), urgent: false });
  }
  return needs;
};

export const DeskProvider: React.FC<{ children: React.ReactNode; initialMonth?: string }> = ({ children, initialMonth }) => {
  const [month, setMonth] = useState(initialMonth || thisMonth());
  const [shown, setShown] = useState<string | null>(null);     // the month whose data is in `state`
  const [state, setState] = useState<Omit<DeskState, 'month'>>({
    loading: true, error: null, planState: null, onboarding: null, productions: [], campaigns: [], posts: [], scheduled: [],
    mediaCount: null, jobs: [], websiteRead: null, nextPlan: null, needs: [], spend: { spent: 0, committed: 0, planned: 0, currency: 'INR', known: false },
  });
  const seq = useRef(0);
  const inFlight = useRef(0);      // loads still running; a poll waits for them instead of superseding

  const load = useCallback(async (m: string) => {
    const mine = ++seq.current;
    inFlight.current += 1;
    try {
      const { start, end } = monthBounds(m);
      const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const [planState, onboarding, sJob, pJob, analytics, scheduled, media, siteRead] = await Promise.all([
        brandKnowledgeApi.getPlan(m),
        safe(brandKnowledgeApi.getOnboarding(), null as OnboardingState | null),
        safe(brandKnowledgeApi.getActiveJob('strategy'), null),
        safe(brandKnowledgeApi.getActiveJob('plan'), null),
        safe(apiClient.get('/api/v1/analytics/campaigns/summary', { params: { time_range: 'lifetime', limit: 100 } }).then(r => r.data?.campaigns || []), [] as any[]),
        // From 2 months back: a campaign live this month may have been published before it started.
        safe(apiClient.get('/api/v1/scheduler/calendar', { params: { start: iso(new Date(start.getFullYear(), start.getMonth() - 2, 1)), end: iso(end) } }).then(r => r.data?.items || []), [] as CalendarItem[]),
        safe(brandKnowledgeApi.getMedia(), null as MediaState | null),
        safe(brandKnowledgeApi.getWebsiteRead(), null as WebsiteRead | null),
      ]);
      const plan = planState.plan && planState.plan.month === m ? planState.plan : null;
      const productions = plan ? await safe(brandKnowledgeApi.getProduction(plan.id).then(r => r.productions), [] as Production[]) : [];
      if (mine !== seq.current) return;

      const numbers: Record<string, CampaignNumbers> = {};
      for (const c of analytics as any[]) if (c.conversation_id) numbers[c.conversation_id] = toNumbers(c);
      const campaigns = plan ? planCampaigns(plan, productions, Array.isArray(scheduled) ? scheduled : [], numbers) : [];
      const posts = plan ? planPosts(plan, productions) : [];
      const nameOf = nameIn(plan);

      const jobs = [jobFromWebsite(siteRead), jobFromBrand(sJob, 'strategy'), jobFromBrand(pJob, 'plan'),
        ...productions.map(p => jobFromProduction(p, nameOf(p), m))].filter(Boolean) as DeskJob[];

      // What needs the owner, most urgent first: this month's plan, then upcoming months' plans
      // (their campaigns are often made ahead), then the strategy.
      const needs: NeedItem[] = plan ? planNeeds(plan, productions, campaigns, posts, m) : [];
      // Strategy approved, no plan for the month yet, none being written: that is the next step.
      // The month to plan next: this one while campaigns have time to learn, else next month (server rule).
      const sugg = planState.suggested_month || m;
      const ahead = planState.months.filter(x => x !== m && x >= thisMonth()).slice(0, 2);
      // The other months' plans, each fetched once and all at the same time (each one is a slow call).
      const others = [...new Set([...(sugg !== m ? [sugg] : []), ...ahead])];
      const fetched = new Map(await Promise.all(others.map(async m2 =>
        [m2, await safe(brandKnowledgeApi.getPlan(m2), null as PlanState | null)] as const)));
      // null when that month couldn't be fetched: a failed or slow fetch is not "no plan"
      let nextPlan: { month: string; plan: MonthlyPlan | null } | null = { month: sugg, plan: sugg === m ? plan : null };
      if (sugg !== m) {
        const s3 = fetched.get(sugg) ?? null;
        nextPlan = s3 ? { month: sugg, plan: s3.plan && s3.plan.month === sugg ? s3.plan : null } : null;
      }
      if (nextPlan && !nextPlan.plan && planState.status === 'ready' && !jobFromBrand(pJob, 'plan')
          && !(onboarding?.readiness.missing || []).some(q => !q.optional))
        needs.push({ id: `plan-write-${sugg}`, kind: 'plan', chip: 'Next step', action: `Plan ${monthLabel(sugg, false)}`,
          title: `Plan ${monthLabel(sugg, false)}`, detail: sugg === m
            ? 'Your strategy is approved. Admeru turns it into this month’s campaigns, posts and check-ins; you approve the plan before anything is made.'
            : `Only a few days are left this month, too few for ads to learn. ${monthLabel(sugg, false)}’s plan can start its campaigns right away.`,
          to: DESK.plan(sugg), urgent: true });
      const aheadPlans = ahead.map(m2 => { const s2 = fetched.get(m2); return s2?.plan && s2.plan.month === m2 ? s2.plan : null; });
      const aheadProds = await Promise.all(aheadPlans.map(p2 => p2
        ? safe(brandKnowledgeApi.getProduction(p2.id).then(r => r.productions), [] as Production[]) : Promise.resolve([] as Production[])));
      ahead.forEach((m2, i) => {
        const p2 = aheadPlans[i];
        if (!p2) return;
        const prods2 = aheadProds[i];
        const camps2 = planCampaigns(p2, prods2, Array.isArray(scheduled) ? scheduled : [], numbers);
        needs.push(...planNeeds(p2, prods2, camps2, planPosts(p2, prods2), m2).map(n => ({ ...n, chip: `${n.chip} · ${monthLabel(m2, false)}` })));
      });
      if (mine !== seq.current) return;
      // A new strategy version waits for the owner's OK; the month's plan keeps following the old one until then.
      const latest = onboarding?.strategy;
      if (latest && latest.status === 'proposed' && !jobFromBrand(sJob, 'strategy')) {
        const behind = !!plan && plan.strategy_version < latest.version;
        needs.push(latest.in_use_version
          ? { id: `strategy-new-${latest.id}`, kind: 'strategy', chip: 'New strategy', action: 'Review strategy',
              title: `A new strategy (version ${latest.version}) is waiting for your OK`,
              detail: behind ? `Your ${monthLabel(m, false)} plan still follows version ${plan!.strategy_version}. Approve the new strategy, then refresh the plan.`
                : `Version ${latest.in_use_version} stays in use until you approve it.`, to: DESK.strategy, urgent: behind }
          : { id: `strategy-new-${latest.id}`, kind: 'strategy', chip: 'Strategy ready', action: 'Review strategy',
              title: 'Your strategy is ready for your OK', detail: 'Approve it and Admeru plans your month from it.', to: DESK.strategy, urgent: true });
      }
      const missingAnswers = (onboarding?.readiness.missing || []).filter(q => !q.optional);
      if (planState.strategy && missingAnswers.length)
        needs.push({ id: 'strategy-answers', kind: 'facts', chip: 'Answers needed', action: `Answer ${missingAnswers.length}`,
          title: `Your strategy and plans need ${missingAnswers.length} more answer${missingAnswers.length === 1 ? '' : 's'}`,
          detail: `${missingAnswers.map(q => q.label).join(', ')}. New plans wait until they're answered.`, to: DESK.business, urgent: true });
      else if (planState.strategy?.stale)
        needs.push(planState.strategy.stale_reason === 'writer'
          ? { id: 'strategy-stale', kind: 'strategy', chip: 'Better strategy available', title: 'Admeru can now write a better strategy', detail: 'It now plans from where your business is today: customers, contacts, followers and how you sell. Refresh when you’re ready.', to: DESK.strategy, urgent: false }
          : { id: 'strategy-stale', kind: 'strategy', chip: 'Out of date', title: 'Your business changed since the strategy was written', detail: 'Refresh the strategy so plans use what’s true now.', to: DESK.strategy, urgent: false });
      needs.sort((x, y) => Number(y.urgent) - Number(x.urgent));

      const currency = plan?.context.currency || 'INR';
      const live = campaigns.filter(c => c.status !== 'skipped');
      const spent = live.reduce((s, c) => s + (c.numbers?.spend || 0), 0);
      const committed = live.filter(c => ['approved', 'scheduled', 'live', 'ended'].includes(c.status)).reduce((s, c) => s + (c.row.brief.budget || 0), 0);
      const planned = live.reduce((s, c) => s + (c.row.brief.budget || 0), 0);

      setShown(m);
      setState({
        loading: false, error: null, planState, onboarding, productions, campaigns, posts, scheduled: Array.isArray(scheduled) ? scheduled : [],
        mediaCount: media ? media.items.length : null, jobs, websiteRead: siteRead, nextPlan, needs,
        spend: { spent, committed: Math.max(0, committed - spent), planned, currency, known: live.some(c => c.numbers?.spend != null) },
      });
    } catch (e: any) {
      if (mine !== seq.current) return;
      setState(s => ({ ...s, loading: false, error: e?.response?.data?.detail || e?.message || 'Couldn’t load this month.' }));
    } finally {
      inFlight.current -= 1;
    }
  }, []);

  // Signed-in pages only: nothing loads (or polls) for signed-out visitors.
  const { user } = useAuth();
  const signedIn = !!user;
  useEffect(() => { if (!signedIn) return; setState(s => ({ ...s, loading: true })); load(month); }, [month, load, signedIn]);

  // Poll while something runs, so progress moves without a reload.
  const busy = signedIn && state.jobs.length > 0;
  useEffect(() => {
    if (!busy) return;
    // A load slower than the interval would otherwise be superseded every time and never land.
    const t = window.setInterval(() => { if (!inFlight.current) load(month); }, 6000);
    return () => window.clearInterval(t);
  }, [busy, month, load]);

  useEffect(() => {
    if (!signedIn) return;
    const onOrg = () => load(month);
    window.addEventListener('organizationChanged', onOrg);
    return () => window.removeEventListener('organizationChanged', onOrg);
  }, [month, load, signedIn]);

  const value = useMemo<DeskContextValue>(
    () => ({ ...state, month: shown ?? month, requestedMonth: month, setMonth, refresh: () => load(month) }),
    [state, shown, month, load]);
  return <DeskContext.Provider value={value}>{children}</DeskContext.Provider>;
};
